import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Plus, Calendar, Clock, MapPin, User, AlertTriangle } from 'lucide-react';
import apiService from '../services/api';

function CreateAppointmentModal({ onClose, onCreated, prefillDate, prefillTime }) {
  const [step, setStep] = useState('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [squareWarning, setSquareWarning] = useState(null);

  // Client search
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchingClients, setSearchingClients] = useState(false);
  const clientSearchTimeout = useRef(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientFirst, setNewClientFirst] = useState('');
  const [newClientLast, setNewClientLast] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');

  // Form fields
  const [technicians, setTechnicians] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedTechId, setSelectedTechId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [date, setDate] = useState(prefillDate || new Date().toLocaleDateString('en-CA'));
  const [time, setTime] = useState(prefillTime || '10:00');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('FL');
  const [zipCode, setZipCode] = useState('');
  const [note, setNote] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [discount, setDiscount] = useState('');
  const [alfFacilities, setAlfFacilities] = useState([]);
  const [selectedAlfId, setSelectedAlfId] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [techData, svcData, alfData] = await Promise.all([
          apiService.get('/amn/staff/', { ordering: 'person__first_name', page_size: 100 }),
          apiService.get('/amn/services/', { active: true, ordering: 'name', page_size: 100 }),
          apiService.get('/amn/alf-facilities/', { page_size: 100 }).catch(() => ({ results: [] })),
        ]);
        const techResults = techData.results || techData;
        const activeTexhs = techResults.filter(t =>
          (t.internal_status === 'active') ||
          (!t.internal_status && (t.square_status === 'active' || t.square_status === 'ACTIVE'))
        );
        setTechnicians(activeTexhs);
        setServices(svcData.results || svcData);
        setAlfFacilities(alfData.results || alfData);
      } catch (err) {
        console.error('Failed to load form data:', err);
      }
    };
    fetchData();
  }, []);

  // Client search with debounce
  useEffect(() => {
    if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
    if (clientSearch.length < 2) {
      setClientResults([]);
      return;
    }
    setSearchingClients(true);
    clientSearchTimeout.current = setTimeout(async () => {
      try {
        const data = await apiService.get('/amn/clients/', {
          search: clientSearch,
          page_size: 10,
          ordering: '-appointment_count',
        });
        setClientResults(data.results || data);
      } catch (err) {
        console.error('Client search error:', err);
      } finally {
        setSearchingClients(false);
      }
    }, 300);
  }, [clientSearch]);

  const selectClient = async (client) => {
    setSelectedClient(client);
    setClientSearch('');
    setClientResults([]);
    const addr = client.effective_address || client.override_address || client.person?.address;
    if (addr) {
      setAddress(addr);
      setCity(client.effective_city || client.override_city || client.person?.city || '');
      setZipCode(client.effective_zip_code || client.override_zip_code || client.person?.zip_code || '');
    } else {
      // No address on client — try last appointment address
      try {
        const appts = await apiService.getAppointments({ client: client.id, ordering: '-date_time', page_size: 1 });
        const lastAppt = (appts.results || appts)?.[0];
        if (lastAppt?.address) {
          setAddress(lastAppt.address);
          setCity(lastAppt.city || '');
          setZipCode(lastAppt.zip_code || '');
          if (lastAppt.state) setState(lastAppt.state);
        }
      } catch (err) {
        // Silent — address is optional
      }
    }
  };

  const toggleService = (serviceId) => {
    setSelectedServiceIds(prev =>
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const selectedServices = services.filter(s => selectedServiceIds.includes(s.id));
  const totalPrice = selectedServices.reduce((sum, s) => sum + parseFloat(s.price || 0) * (serviceQuantities[s.id] || 1), 0);

  const hasClient = selectedClient || (showNewClient && newClientFirst.trim());
  const canSubmit = hasClient && selectedTechId && selectedServiceIds.length > 0 && date && time;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSquareWarning(null);

    try {
      const dateTime = `${date}T${time}:00`;
      const payload = {
        technician_id: parseInt(selectedTechId),
        service_ids: selectedServiceIds,
        service_quantities: serviceQuantities,
        date_time: dateTime,
        address,
        city,
        state,
        zip_code: zipCode,
        additional_note: note,
        internal_notes: internalNotes,
        discount: discount ? parseFloat(discount) : 0,
        alf_facility_id: selectedAlfId ? parseInt(selectedAlfId) : null,
      };

      if (selectedClient) {
        payload.client_id = selectedClient.id;
      } else {
        payload.new_client = {
          first_name: newClientFirst.trim(),
          last_name: newClientLast.trim(),
          phone: newClientPhone.trim(),
          email: newClientEmail.trim(),
        };
      }

      const result = await apiService.createAppointmentFull(payload);

      if (result.square_push_status === 'failed') {
        setSquareWarning(result.square_push_error || 'Square push failed');
      }

      setStep('success');
      if (onCreated) onCreated(result.appointment);
    } catch (err) {
      setError(err.message || 'Failed to create appointment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto pt-8 pb-8">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Appointment
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === 'success' ? (
          <div className="p-6 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">Appointment Created</p>
            {squareWarning && (
              <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>Square sync failed: {squareWarning}. Appointment exists in our system only.</span>
              </div>
            )}
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 text-sm font-medium text-white bg-red-800 rounded-md hover:bg-red-900"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-xs">
                {error}
              </div>
            )}

            {/* Client Search / Create */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">
                  <User className="w-3 h-3 inline mr-1" />Client
                </label>
                {!selectedClient && (
                  <button
                    type="button"
                    onClick={() => { setShowNewClient(!showNewClient); setClientSearch(''); setClientResults([]); }}
                    className="text-xs font-medium text-red-700 hover:text-red-900"
                  >
                    {showNewClient ? 'Search existing' : '+ New client'}
                  </button>
                )}
              </div>
              {selectedClient ? (
                <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                  <span className="text-sm font-medium text-gray-900">
                    {selectedClient.person_name || (selectedClient.person?.first_name + ' ' + (selectedClient.person?.last_name || ''))}
                    {(selectedClient.phone_number || selectedClient.person?.phone_number) && (
                      <span className="text-gray-400 ml-2 text-xs">{selectedClient.phone_number || selectedClient.person?.phone_number}</span>
                    )}
                  </span>
                  <button type="button" onClick={() => setSelectedClient(null)} className="text-xs text-red-600 hover:text-red-800">
                    Change
                  </button>
                </div>
              ) : showNewClient ? (
                <div className="bg-gray-50 rounded-md p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newClientFirst}
                      onChange={e => setNewClientFirst(e.target.value)}
                      placeholder="First name *"
                      className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={newClientLast}
                      onChange={e => setNewClientLast(e.target.value)}
                      placeholder="Last name"
                      className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                    />
                  </div>
                  <input
                    type="tel"
                    value={newClientPhone}
                    onChange={e => setNewClientPhone(e.target.value)}
                    placeholder="Phone number"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                  />
                  <input
                    type="email"
                    value={newClientEmail}
                    onChange={e => setNewClientEmail(e.target.value)}
                    placeholder="Email (optional)"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                  />
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={e => setClientSearch(e.target.value)}
                    placeholder="Search by name or phone..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800 focus:border-transparent"
                    autoFocus
                  />
                  {clientResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {clientResults.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => selectClient(client)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium">{client.person_name || client.person?.first_name + ' ' + (client.person?.last_name || '')}</span>
                          {(client.phone_number || client.person?.phone_number) && (
                            <span className="text-gray-400 ml-2 text-xs">{client.phone_number || client.person?.phone_number}</span>
                          )}
                          <span className="text-gray-400 text-xs ml-2">({client.appointment_count || 0} appts)</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchingClients && clientSearch.length >= 2 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-xs text-gray-500">
                      Searching...
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Technician */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Technician</label>
              <select
                value={selectedTechId}
                onChange={e => setSelectedTechId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
              >
                <option value="">Select technician...</option>
                {technicians.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.person?.first_name} {t.person?.last_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <Calendar className="w-3 h-3 inline mr-1" />Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  <Clock className="w-3 h-3 inline mr-1" />Time
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                />
              </div>
            </div>

            {/* Services */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Services {selectedServiceIds.length > 0 && (
                  <span className="text-green-700 font-normal">({selectedServiceIds.length} selected - ${totalPrice.toFixed(2)})</span>
                )}
              </label>
              <div className="border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                {services.map(svc => {
                  const isSelected = selectedServiceIds.includes(svc.id);
                  const qty = serviceQuantities[svc.id] || 1;
                  return (
                  <div
                    key={svc.id}
                    className={`flex items-center justify-between px-3 py-1.5 text-sm border-b border-gray-100 last:border-0 ${
                      isSelected ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <label className="flex items-center gap-2 cursor-pointer flex-1">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          toggleService(svc.id);
                          if (!isSelected) setServiceQuantities(prev => ({...prev, [svc.id]: 1}));
                        }}
                        className="w-3.5 h-3.5 text-red-800 rounded"
                      />
                      <span className={isSelected ? 'font-medium' : ''}>{svc.name}</span>
                    </label>
                    {isSelected && (
                      <div className="flex items-center gap-1.5 mr-2">
                        <button type="button" onClick={() => setServiceQuantities(prev => ({...prev, [svc.id]: Math.max(1, (prev[svc.id] || 1) - 1)}))}
                          className="w-5 h-5 rounded bg-gray-200 text-gray-700 text-xs flex items-center justify-center hover:bg-gray-300">−</button>
                        <span className="text-xs font-semibold w-4 text-center">{qty}</span>
                        <button type="button" onClick={() => setServiceQuantities(prev => ({...prev, [svc.id]: (prev[svc.id] || 1) + 1}))}
                          className="w-5 h-5 rounded bg-gray-200 text-gray-700 text-xs flex items-center justify-center hover:bg-gray-300">+</button>
                      </div>
                    )}
                    <span className="text-xs text-gray-500">${(parseFloat(svc.price || 0) * (isSelected ? qty : 1)).toFixed(2)}</span>
                  </div>
                  );
                })}
              </div>
            </div>

            {/* Address */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                <MapPin className="w-3 h-3 inline mr-1" />Address
              </label>
              <input
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Street address"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800 mb-1"
              />
              <div className="grid grid-cols-3 gap-1">
                <input
                  type="text"
                  value={city}
                  onChange={e => setCity(e.target.value)}
                  placeholder="City"
                  className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                />
                <input
                  type="text"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  placeholder="State"
                  className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                />
                <input
                  type="text"
                  value={zipCode}
                  onChange={e => setZipCode(e.target.value)}
                  placeholder="Zip"
                  className="px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                />
              </div>
            </div>

            {/* ALF Facility (shown when ALF service selected) */}
            {selectedServices.some(s => s.is_alf || (s.name && s.name.toLowerCase().includes('alf'))) && alfFacilities.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">ALF Facility</label>
                <select
                  value={selectedAlfId}
                  onChange={e => setSelectedAlfId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
                >
                  <option value="">Select facility (optional)...</option>
                  {alfFacilities.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Discount */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Discount ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={discount}
                onChange={e => setDiscount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
              />
            </div>

            {/* Notes (client-visible, syncs to Square) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Client Notes</label>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Visible to client (e.g., gate code, parking)..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
              />
            </div>

            {/* Internal Notes (staff-only) */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Internal Notes</label>
              <textarea
                value={internalNotes}
                onChange={e => setInternalNotes(e.target.value)}
                placeholder="Staff-only notes (not visible to client)..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-red-800"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full py-2.5 text-sm font-medium text-white bg-red-800 rounded-md hover:bg-red-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? 'Creating...' : 'Create Appointment'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default CreateAppointmentModal;
